import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;

import store from 'store/Store';
import Location from 'utils/Location';
import userUtils from 'utils/User';
import Actions from 'actions/Actions';
import userActions from 'actions/UserActions';
import { linkedAccountDetailsSelector } from 'selectors/user/beautyInsiderAccount/linkedAccountDetailsSelector';
import { authSelector } from 'selectors/auth/authSelector';
import profileIdSelector from 'selectors/user/profileIdSelector';
import getPartnerContent from 'services/api/loyaltyConnection/getPartnerContent';
import LoyaltyConnectionLanding from 'components/LoyaltyConnection/LoyaltyConnectionLanding/LoyaltyConnectionLanding';
import LinkAccountModal from 'components/LoyaltyConnection/LinkAccountModal/LinkAccountModal';
import LinkErrorModal from 'components/LoyaltyConnection/LinkErrorModal/LinkErrorModal';
import {
    getPartnerSlug, isPartnerLinked, getContentItems, isAuthorizedLCUser, UNAUTHORIZED_REDIRECT_URL, LINK_RESULT_PARAM
} from 'components/LoyaltyConnection/constants';

const MODAL = {
    NONE: 'none',
    LINK: 'link',
    ERROR: 'error'
};

/**
 * Sephora x Starbucks loyalty partnership landing page (LC-145).
 *
 * Container responsibilities:
 *  - Gate access: only authorized Loyalty Connection users may view the page
 *    (client-side port of RequestValidator#isAuthorizedLCUser). Anonymous users
 *    are prompted to sign in and re-checked; unauthorized users are redirected.
 *  - Client-side fetch the content-page-exp CMS content (via the same-origin
 *    /api/content proxy) and render it.
 *  - Own the account-link flow: Link Account consent modal -> initiateAccountLink
 *    mutation -> redirect the window to the partner OAuth URL. The provider
 *    redirects back to this page: on success the linked state is detected on load;
 *    on failure it returns with ?isSuccess=false, which shows the error popup (the
 *    param is then stripped from the URL without reloading).
 */
const LoyaltyConnectionLandingPage = () => {
    const [contentSections, setContentSections] = useState([]);
    const [activeModal, setActiveModal] = useState(MODAL.NONE);
    const linkedAccounts = useSelector(linkedAccountDetailsSelector);
    // Reactive signed-in flag (auth.profileStatus === LOGGED_IN) so effects re-run
    // when the member signs in after mount (e.g. via the header), not just on mount.
    const isSignedIn = userUtils.isSignedIn(useSelector(authSelector));
    const [isLinked, setIsLinked] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const profileId = useSelector(profileIdSelector);
    const signInPromptedRef = useRef(false);
    const dispatch = useDispatch();

    // Authorization gate (client-side port of RequestValidator#isAuthorizedLCUser).
    // Re-runs when the profile id changes (profileId) so a signed-in user is not
    // redirected before their profile is available, and so the gate re-evaluates on
    // session changes (e.g. sign-out). profileId is the clientId the backend
    // validates the beta-user list against.
    useEffect(() => {
        if (isAuthorizedLCUser(profileId, getPartnerSlug())) {
            setIsAuthorized(true);

            return;
        }

        // Not authorized for the current session. If we were authorized before (e.g.
        // the member signed out while staying on this route, so profileId is now null),
        // clear it and send them away instead of leaving the gated page rendered.
        if (isAuthorized) {
            setIsAuthorized(false);
            Location.setLocation(UNAUTHORIZED_REDIRECT_URL);

            return;
        }

        if (userUtils.isSignedIn()) {
            // Wait until the profile (profileId) has loaded before deciding; the
            // getUserFull effect below populates it and re-triggers this effect.
            if (profileId != null) {
                Location.setLocation(UNAUTHORIZED_REDIRECT_URL);
            }

            return;
        }

        // Anonymous: prompt sign-in once, then re-check after the profile loads.
        if (signInPromptedRef.current) {
            return;
        }

        signInPromptedRef.current = true;
        dispatch(
            Actions.showSignInModal({
                isOpen: true,
                callback: () => {
                    dispatch(userActions.getUserFull(null, null, { forceLinkedAccountDetails: true }, undefined, true))
                        .then(() => {
                            if (isAuthorizedLCUser(profileIdSelector(store.getState()), getPartnerSlug())) {
                                setIsAuthorized(true);
                            } else {
                                Location.setLocation(UNAUTHORIZED_REDIRECT_URL);
                            }
                        })
                        .catch(() => Location.setLocation(UNAUTHORIZED_REDIRECT_URL));
                },
                // If the member dismisses the sign-in modal without signing in, don't
                // leave them stranded on the gated (null) page — redirect them away.
                errback: () => Location.setLocation(UNAUTHORIZED_REDIRECT_URL)
            })
        );
    }, [profileId, isAuthorized, dispatch]);

    // Derive linked state from the store (same linked-partners data as Account
    // Settings). Computed in an effect to stay SSR-safe (slug reads window) and to
    // react to store updates such as sign-in.
    useEffect(() => {
        setIsLinked(isPartnerLinked(linkedAccounts, getPartnerSlug()));
    }, [linkedAccounts]);

    // If the partner turns out to be already linked, close any open link/OAuth modal.
    useEffect(() => {
        if (isLinked) {
            setActiveModal(MODAL.NONE);
        }
    }, [isLinked]);

    // Force-load the linked-partner details for signed-in members so the linked
    // state can be determined (they are otherwise only loaded on My Account).
    // Depends on the reactive signed-in flag so it also runs if the member signs in
    // after mount via another entry point (e.g. the header) while staying on the page.
    useEffect(() => {
        if (isSignedIn) {
            dispatch(userActions.getUserFull(null, null, { forceLinkedAccountDetails: true }, undefined, true));
        }
    }, [isSignedIn, dispatch]);

    // On return from a failed link (partner OAuth failureUrl or a client-side link
    // error), the URL carries ?isSuccess=false. Show the error popup, then strip
    // the param from the address bar without reloading the page.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get(LINK_RESULT_PARAM) === 'false') {
            setActiveModal(MODAL.ERROR);
            params.delete(LINK_RESULT_PARAM);
            const query = params.toString();
            const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
            window.history.replaceState(null, '', newUrl);
        }
    }, []);

    // Fetch the CMS content only once the user is authorized, so unauthorized users
    // (who are redirected) don't trigger an unnecessary content call.
    useEffect(() => {
        if (!isAuthorized) {
            return undefined;
        }

        let isMounted = true;

        getPartnerContent()
            .then(cmsData => {
                if (!isMounted) {
                    return;
                }

                // The typed content items live inside contentSections[].content;
                // flatten them into a single ordered list for the landing view.
                setContentSections(getContentItems(cmsData));
            })
            .catch(error => {
                // Non-fatal: content sections simply stay empty.
                Sephora.logger.error('[LoyaltyConnection] Failed to load Starbucks CMS content:', error);
            });

        return () => {
            isMounted = false;
        };
    }, [isAuthorized]);

    const closeModal = () => setActiveModal(MODAL.NONE);

    const openLinkModal = () => setActiveModal(MODAL.LINK);

    const handleLinkClick = () => {
        if (userUtils.isSignedIn()) {
            openLinkModal();

            return;
        }

        // Not signed in: prompt sign-in (stays on this page). After a successful
        // sign-in, if the partner is already linked the linked view renders
        // reactively; otherwise start the linking flow.
        dispatch(
            Actions.showSignInModal({
                isOpen: true,
                callback: () => {
                    // After sign-in, refresh the linked-partner details, then decide:
                    // already linked -> linked view renders reactively; else start linking.
                    dispatch(userActions.getUserFull(null, null, { forceLinkedAccountDetails: true }, undefined, true))
                        .then(() => {
                            if (!isPartnerLinked(linkedAccountDetailsSelector(store.getState()), getPartnerSlug())) {
                                openLinkModal();
                            }
                        })
                        .catch(() => openLinkModal());
                }
            })
        );
    };

    // Redirect the whole window to the partner OAuth URL. The provider redirects
    // back to this page (successUrl/failureUrl), where the linked state is detected.
    const handleOAuthUrl = partnerOAuthUrl => {
        if (partnerOAuthUrl) {
            Location.setLocation(partnerOAuthUrl);
        }
    };

    // Gate: render nothing until the user is authorized (while checking, prompting
    // sign-in, or redirecting an unauthorized user).
    if (!isAuthorized) {
        return null;
    }

    return (
        <>
            <LoyaltyConnectionLanding
                contentSections={contentSections}
                isLinked={isLinked}
                onLinkClick={handleLinkClick}
            />

            <LinkAccountModal
                isOpen={activeModal === MODAL.LINK}
                onDismiss={closeModal}
                onOAuthUrl={handleOAuthUrl}
            />

            <LinkErrorModal
                isOpen={activeModal === MODAL.ERROR}
                onDismiss={closeModal}
            />
        </>
    );
};

export default wrapFunctionalComponent(LoyaltyConnectionLandingPage, 'LoyaltyConnectionLandingPage');
